import { create } from 'zustand';
import { notificationService } from '@soundbyte/services';
import type { Notification } from '@soundbyte/types';

interface NotificationState {
  // State
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;

  // Actions
  fetchNotifications: () => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  clearAll: () => Promise<void>;
  addNotification: (notification: Notification) => void;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  // Initial state
  notifications: [],
  unreadCount: 0,
  isLoading: false,

  // Fetch all notifications
  fetchNotifications: async () => {
    set({ isLoading: true });

    const response = await notificationService.getAll();

    if (response.success && response.data) {
      const unread = response.data.filter((n) => !n.read).length;
      set({
        notifications: response.data,
        unreadCount: unread,
        isLoading: false,
      });
    } else {
      set({ isLoading: false });
    }
  },

  // Fetch unread count only
  fetchUnreadCount: async () => {
    const response = await notificationService.getUnreadCount();

    if (response.success && response.data) {
      set({ unreadCount: response.data.count });
    }
  },

  // Mark single notification as read
  markAsRead: async (id) => {
    const response = await notificationService.markAsRead(id);

    if (response.success) {
      set({
        notifications: get().notifications.map((n) =>
          n.id === id ? { ...n, read: true } : n
        ),
        unreadCount: Math.max(0, get().unreadCount - 1),
      });
    }
  },

  // Mark all as read
  markAllAsRead: async () => {
    const response = await notificationService.markAllAsRead();

    if (response.success) {
      set({
        notifications: get().notifications.map((n) => ({ ...n, read: true })),
        unreadCount: 0,
      });
    }
  },

  // Delete notification
  deleteNotification: async (id) => {
    const notification = get().notifications.find((n) => n.id === id);
    const response = await notificationService.delete(id);

    if (response.success) {
      set({
        notifications: get().notifications.filter((n) => n.id !== id),
        unreadCount: notification && !notification.read
          ? Math.max(0, get().unreadCount - 1)
          : get().unreadCount,
      });
    }
  },

  // Clear all notifications
  clearAll: async () => {
    const response = await notificationService.clearAll();

    if (response.success) {
      set({ notifications: [], unreadCount: 0 });
    }
  },

  // Add notification locally (for real-time updates)
  addNotification: (notification) => {
    set({
      notifications: [notification, ...get().notifications],
      unreadCount: notification.read ? get().unreadCount : get().unreadCount + 1,
    });
  },
}));

// Selector hooks
export const useNotifications = () => useNotificationStore((state) => state.notifications);
export const useUnreadCount = () => useNotificationStore((state) => state.unreadCount);
